import { useQuery } from '@tanstack/react-query';
import {
  fetchBoltOn,
  fetchBoltOnFootprint,
  fetchBoltOnMulti,
  type BoltOnQuery,
  type BoltOnResponse,
  type BoltOnFootprint,
  type BoltOnMultiQuery,
  type BoltOnMemberCatchment,
} from './client.js';

// Bolt-On catchment hook. Fires only once a seed is set (a pin dropped or a
// company footprint supplied) — `enabled` gates it so the panel doesn't query
// before there's a point to centre on. Keyed on the full query (seed + band +
// subsegment + mode), so changing any of them refetches; pan/zoom does not.

export function useBoltOn(query: BoltOnQuery | null, opts: { enabled?: boolean } = {}) {
  return useQuery<BoltOnResponse, Error>({
    queryKey: ['bolt-on', query] as const,
    queryFn: () => fetchBoltOn(query!),
    staleTime: 60_000,
    enabled: (opts.enabled ?? true) && query !== null,
  });
}

// Multi-point catchment for the Roll-Up Foundation coverage view — one
// catchment per member (each in its own subsegment). Fires only when there are
// members; keyed on the full query so changing the travel-time/revenue override
// refetches.
export function useBoltOnMulti(query: BoltOnMultiQuery | null, opts: { enabled?: boolean } = {}) {
  return useQuery<{ members: BoltOnMemberCatchment[] }, Error>({
    queryKey: ['bolt-on-multi', query] as const,
    queryFn: () => fetchBoltOnMulti(query!),
    staleTime: 60_000,
    enabled: (opts.enabled ?? true) && query !== null && (query?.members.length ?? 0) > 0,
  });
}

/** Footprint seed for the "Find bolt-ons near this company" entry. */
export function useBoltOnFootprint(entityId: string | null) {
  return useQuery<BoltOnFootprint, Error>({
    queryKey: ['bolt-on-footprint', entityId] as const,
    queryFn: () => fetchBoltOnFootprint(entityId!),
    staleTime: 5 * 60_000,
    enabled: entityId !== null,
  });
}
